// Task #3041 — Componente unico "faccia del PA" (icona ↔ avatar).
//
// Legge il flag personale e decide cosa renderizzare:
// - avatar DISABILITATO (default) → l'icona attuale del PA, con lo stesso
//   markup di prima (cerchio colorato + icona Lucide): zero regressioni;
// - avatar ABILITATO → l'avatar DiceBear scelto (o il default deterministico
//   da `defaultSeed` se mai configurato) con un layer di stato leggero
//   (anello colorato + dot pulsante + "sta scrivendo") pilotato dallo stato
//   del Workspace: idle / ragionamento / esecuzione tool / attesa approvazione
//   / completato / errore.

import React, { useMemo } from 'react';
import { defaultPaAvatarConfig, paAvatarCacheKey, paAvatarDataUri } from './paAvatarStyles';
import type { PaAvatarConfig } from '@easyflow/shared';

export type PaFaceState = 'idle' | 'thinking' | 'executing' | 'awaiting_approval' | 'done' | 'error';

const STATE_COLOR: Record<PaFaceState, string | null> = {
  idle: null,
  thinking: '#8b5cf6',
  executing: '#f59e0b',
  awaiting_approval: '#f97316',
  done: '#10b981',
  error: '#ef4444',
};

const PULSING_STATES: ReadonlySet<PaFaceState> = new Set(['thinking', 'executing', 'awaiting_approval']);

export interface PaFaceProps {
  /** Flag personale: OFF = icona attuale (comportamento identico a oggi). */
  avatarEnabled: boolean;
  /** Config scelta dall'utente; null = default deterministico da `defaultSeed`. */
  avatarConfig: PaAvatarConfig | null;
  /** Seed deterministico di fallback (mai "icona rotta"). */
  defaultSeed: string;
  /** Colore tema dell'assistente (sfondo del cerchio in modalità icona). */
  color: string;
  /** Icona attuale del PA: stato OFF e fallback. */
  FallbackIcon: React.ComponentType<{ className?: string }>;
  /** Stato corrente del PA per il layer di stato (solo ad avatar attivo). */
  state?: PaFaceState;
  /** Lato in px del cerchio (es. 28 = w-7). */
  size: number;
  /** Classe di arrotondamento (default cerchio pieno). */
  rounded?: string;
  /** Dimensione dell'icona fallback (default proporzionata a `size`). */
  iconClassName?: string;
  /** Classi extra sul wrapper (margini ecc.). */
  className?: string;
  /** Disattiva il layer di stato (es. accanto ai messaggi storici). */
  showStateLayer?: boolean;
}

export const PaFace: React.FC<PaFaceProps> = ({
  avatarEnabled,
  avatarConfig,
  defaultSeed,
  color,
  FallbackIcon,
  state = 'idle',
  size,
  rounded = 'rounded-full',
  iconClassName,
  className = '',
  showStateLayer = true,
}) => {
  const cfg = avatarEnabled ? (avatarConfig ?? defaultPaAvatarConfig(defaultSeed)) : null;
  const cfgKey = cfg ? paAvatarCacheKey(cfg) : null;
  const dataUri = useMemo(() => (cfg ? paAvatarDataUri(cfg) : null), [cfgKey]); // eslint-disable-line react-hooks/exhaustive-deps -- cfgKey è la cache key deterministica di cfg: dep su cfg rigenererebbe il data URI a ogni render

  if (!cfg || !dataUri) {
    // Stato OFF / fallback: markup identico all'icona attuale del PA.
    const fallbackIconClass = iconClassName ?? (size >= 48 ? 'h-7 w-7' : size >= 32 ? 'h-4 w-4' : 'h-3.5 w-3.5');
    return (
      <div
        className={`${rounded} flex items-center justify-center text-white shrink-0 ${className}`}
        style={{ width: size, height: size, backgroundColor: color }}
      >
        <FallbackIcon className={fallbackIconClass} />
      </div>
    );
  }

  const ringColor = showStateLayer ? STATE_COLOR[state] : null;
  const pulsing = showStateLayer && PULSING_STATES.has(state);
  const dotSize = Math.max(8, Math.round(size / 4.5));
  const showTypingPill = showStateLayer && state === 'thinking' && size >= 44;

  return (
    <div className={`relative shrink-0 ${className}`} style={{ width: size, height: size }}>
      <img
        src={dataUri}
        alt=""
        aria-hidden="true"
        draggable={false}
        className={`${rounded} h-full w-full select-none bg-white/90 object-cover`}
        style={ringColor ? { boxShadow: `0 0 0 2px ${ringColor}` } : undefined}
      />
      {ringColor && !showTypingPill && (
        <span
          className={`absolute -bottom-0.5 -right-0.5 rounded-full border-2 border-card ${pulsing ? 'animate-pulse' : ''}`}
          style={{ width: dotSize, height: dotSize, backgroundColor: ringColor }}
        />
      )}
      {showTypingPill && (
        <span
          className="absolute -bottom-1 -right-1 flex items-center gap-[2px] rounded-full border border-border bg-card px-1 py-[3px]"
          title="Sta scrivendo"
        >
          {[0, 1, 2].map(i => (
            <span
              key={i}
              className="h-1 w-1 animate-bounce rounded-full"
              style={{ backgroundColor: STATE_COLOR.thinking ?? undefined, animationDelay: `${i * 150}ms` }}
            />
          ))}
        </span>
      )}
    </div>
  );
};
